// ============================================================
// TICK ORCHESTRATOR
// ============================================================
//
// Runs the 3-Tick Execution Phase (SRS §3.3) as a pure function:
//   executeExec(state, commands) → { newState, events }
//
// Internally the function clones state once and mutates the clone,
// so callers retain referential transparency while the inner
// helpers (spell/movement/combat/contest) stay intrusive.
//
// Step ordering inside each tick (Session 6a + a-2 terrain — SRS §3.3):
//   A. Friendly spells (heal + buff + defense*) — both teams simultaneously
//   B. Movement (two sub-phases)
//   C. Combat (Mage → Archer → Cavalry → Infantry, simultaneous per group)
//      ↳ tickDefPhys reduces incoming damage here
//      ↳ Archer cover: target on grass / burningGrass halves damage
//   D. Hostile spells (damage + debuff)         — both teams simultaneously
//      ↳ tickDefMag reduces incoming damage here
//      ↳ Fire / Fireball ignites grass in its radius (a-2)
//   E. Contest resolve (lower HP retreats)
//   F. Terrain DoT (a-2): units standing on burningGrass take
//      ceil(maxSoldiers × 0.15) magical damage, reduced by tickDefMag.
//      ↳ AT END: zero buffAtk / tickDefPhys / tickDefMag on every unit
//        so Bless and Shield Wall are tick-scoped (Session 7).
//
// * Defense added Session 7 (2026-05-06): Wind / Shield Wall is a
//   friendly-phase spell that grants tick-scoped phys/mag reductions
//   to allies in radius.
//
// After EACH sub-step we check game-end. If a base hits 0 hp anywhere
// in the sequence, we emit `gameEnd` immediately and abort the rest of
// the tick — no further sub-steps, no future ticks. This matches
// Session 6a item 2: "達成勝利條件即時結束（不要再播後續動畫）".

import type { BattleUnit, Base, Pos } from '../BattleUnit.ts';
import { UCFG } from '../constants.ts';
// 平衡性數字 (TERRAIN_BURN_DOT_RATIO) 搬到 balance.ts；這裡 re-export 維持 back-compat。
import { TERRAIN_BURN_DOT_RATIO } from '../balance.ts';
import { cloneState } from './clone.ts';
import { resolveSpells, _recomputeScalars } from './spell.ts';
import { resolveMovement } from './movement.ts';
import { resolveCombat } from './combat.ts';
import { resolveContest } from './contest.ts';
import { applyDamageToUnit } from './damage.ts';
import { getTerrainAt } from './terrain.ts';
import type {
  BattleEvent,
  ServerState,
  SpellCommand,
  TerrainDotEvent,
  TerrainGrowEvent,
  TerrainRainEvent,
  TickCommands,
} from './types.ts';
import { spellsForTeam } from './types.ts';

// Re-export for back-compat. 想改數值請編輯 src/balance.ts。
export { TERRAIN_BURN_DOT_RATIO };

export interface ExecResult {
  newState: ServerState;
  events: BattleEvent[];
  /** -1 if battle still going, 0/1 if winner, -2 if draw. */
  winner: number;
}

export function executeExec(state: ServerState, cmds: TickCommands): ExecResult {
  const next = cloneState(state);
  const events: BattleEvent[] = [];

  for (let tick = 0; tick < 3; tick++) {
    events.push({ type: 'tickLabel', tick, dur: 1.8 });

    // Build the cast list once per tick — both phases reuse it; the spell
    // type filter inside resolveSpells ignores casts that don't match the
    // requested phase.
    const casts: { team: number; cmd: SpellCommand }[] = [];
    for (const team of [0, 1] as const) {
      const cmd = spellsForTeam(cmds, team)[tick];
      if (cmd) casts.push({ team, cmd });
    }

    // Step A: Friendly Spells (heal + buff + Session 9 whirlwind-friendly) — tick START.
    // Whirlwind needs `bases` so it can reject destinations that would
    // land a unit on a base tile (design Q3 — bases are unaffected).
    const friendly = resolveSpells(next.units, casts, 'friendly', { bases: next.bases });
    if (friendly.length > 0) {
      events.push({
        type: 'spell',
        tick,
        dur: 2.2,
        phase: 'friendly',
        spells: friendly,
      });
    }
    if (_emitGameEndIfOver(next, events)) {
      return { newState: next, events, winner: _winnerFromTail(events) };
    }

    // Step B: Movement — pass terrain so puddles slow Cavalry (b 段).
    const move = resolveMovement(next.units, next.bases, next.terrain);
    if (move.moves.length > 0 || move.contested.length > 0) {
      events.push({
        type: 'move',
        tick,
        dur: 2.0,
        moves: move.moves,
        contested: move.contested,
      });
    }
    if (_emitGameEndIfOver(next, events)) {
      return { newState: next, events, winner: _winnerFromTail(events) };
    }

    // Step C: Combat — pass terrain so Archer cover discount applies.
    const groups = resolveCombat(next.units, next.bases, next.terrain);
    if (groups.length > 0) {
      events.push({ type: 'combat', tick, dur: 1.0, groups });
    }
    if (_emitGameEndIfOver(next, events)) {
      return { newState: next, events, winner: _winnerFromTail(events) };
    }

    // Step D: Hostile Spells (damage + debuff) — tick END, before contest.
    // a-2 — also pass terrain so Fire ignites grass; ignitedCells captures
    // the grass→burningGrass transitions for the terrainIgnite event.
    // Session 11 — Grow / Rain also paint terrain; grownCells / rainedCells
    // capture none→grass / none→puddle transitions for separate events.
    const ignitedCells: Pos[] = [];
    // Session 11 — Grow/Rain transitions ship `to` per cell so the client
    // applies the change idempotently regardless of its predicted state.
    const grownCells: TerrainGrowEvent['cells'] = [];
    const rainedCells: TerrainRainEvent['cells'] = [];
    // Session 12 — Rockfall paints boulders, Fire/Lightning shatter them.
    const boulderFallCells: Pos[] = [];
    const boulderBreakCells: Pos[] = [];
    const hostile = resolveSpells(next.units, casts, 'hostile', {
      terrain: next.terrain,
      ignitedCells,
      grownCells,
      rainedCells,
      boulderFallCells,
      boulderBreakCells,
      // Session 9 — whirlwind-hostile lives in Step D and needs bases
      // for the base-tile blocking check (same rationale as Step A).
      bases: next.bases,
    });
    if (hostile.length > 0) {
      events.push({
        type: 'spell',
        tick,
        dur: 2.2,
        phase: 'hostile',
        spells: hostile,
      });
    }
    if (ignitedCells.length > 0) {
      // c 段 — the client now spawns ignition particles inside the
      // preceding `spell` event so flames erupt simultaneously with the
      // fireball. This event becomes a state-only marker; its dur is
      // ~0 so the EXEC timeline doesn't pause visibly between the
      // fireball and the next sub-step.
      events.push({
        type: 'terrainIgnite',
        tick,
        dur: 0.05,
        cells: ignitedCells,
      });
    }
    // Session 11 — Grow / Rain mirror the terrainIgnite pattern: paint
    // animation is spawned synchronously inside the preceding `spell`
    // event (via Game._spawnGrowFx / _spawnRainFx), this event is the
    // authoritative state-mutation marker. Tiny dur so it doesn't add
    // perceptible pause to the EXEC timeline. Suppressed entirely if
    // the cast landed only on already-occupied terrain (empty cells list).
    if (grownCells.length > 0) {
      events.push({ type: 'terrainGrow', tick, dur: 0.05, cells: grownCells });
    }
    if (rainedCells.length > 0) {
      events.push({ type: 'terrainRain', tick, dur: 0.05, cells: rainedCells });
    }
    // Session 12 — Rockfall paint + Fire/Lightning boulder break events.
    // Same idempotent-spawn pattern as terrainIgnite/Grow/Rain: client
    // synchronously predicts the terrain change inside the preceding
    // `spell` event (so the boulder appears at the spell-hit frame), then
    // this authoritative event arrives and (re)applies the state to
    // overwrite any client-prediction drift. Tiny dur so EXEC timeline
    // doesn't gain a perceptible pause. Suppressed if empty.
    if (boulderFallCells.length > 0) {
      events.push({ type: 'terrainBoulderFall', tick, dur: 0.05, cells: boulderFallCells });
    }
    if (boulderBreakCells.length > 0) {
      events.push({ type: 'terrainBoulderBreak', tick, dur: 0.05, cells: boulderBreakCells });
    }
    if (_emitGameEndIfOver(next, events)) {
      return { newState: next, events, winner: _winnerFromTail(events) };
    }

    // Step E: Contest resolve (only if we had contested tiles this tick).
    // Note: a contested unit that died in steps C or D drops out of the
    // contest naturally — resolveContest skips dead units.
    if (move.contested.length > 0) {
      const res = resolveContest(next.units, move.contested);
      if (res.retreats.length > 0 || res.stays.length > 0) {
        events.push({
          type: 'contestResolve',
          tick,
          dur: 1.2,
          retreats: res.retreats,
          stays: res.stays,
        });
      }
    }
    if (_emitGameEndIfOver(next, events)) {
      return { newState: next, events, winner: _winnerFromTail(events) };
    }

    // Step F (a-2): Terrain DoT.
    //
    // After contest has settled the final positions for this tick, any
    // unit standing on a `'burningGrass'` cell takes magical-class
    // damage = ceil(maxSoldiers × TERRAIN_BURN_DOT_RATIO), reduced by
    // tickDefMag (DOT is treated as magical for shielding purposes —
    // design Q8). We process this BEFORE zeroing tickDefMag so a Shield
    // Wall cast in Step A of THIS tick still protects the standee.
    const dotApplies: TerrainDotEvent['applies'] = [];
    if (next.terrain) {
      for (const u of next.units) {
        if (u.dead) continue;
        const cell = getTerrainAt(next.terrain, u.pos.x, u.pos.y);
        if (cell !== 'burningGrass') continue;
        const cfg = UCFG[u.type];
        const maxSoldiers = cfg ? cfg.hp : u.totalSoldiers;
        const rawDmg = Math.ceil(maxSoldiers * TERRAIN_BURN_DOT_RATIO);
        const reduce = u.tickDefMag || 0;
        const finalDmg = reduce > 0 ? Math.round(rawDmg * (1 - reduce)) : rawDmg;
        // applyDamageToUnit clamps at totalSoldiers=0 and flips dead.
        const delta = applyDamageToUnit(u, finalDmg);
        dotApplies.push({
          unitId: u.id,
          dmg: delta.dmg,
          died: delta.died,
          totalSoldiers: delta.totalSoldiers,
        });
      }
    }
    if (dotApplies.length > 0) {
      events.push({
        type: 'terrainDot',
        tick,
        dur: 1.2,
        applies: dotApplies,
      });
    }
    // DoT can finish off the last surviving unit on a side — game-end
    // check belongs after Step F too.
    if (_emitGameEndIfOver(next, events)) {
      return { newState: next, events, winner: _winnerFromTail(events) };
    }

    // Session 7 + Session 10 — modifier lifecycle end-of-tick.
    //
    // The old code zeroed buffAtk / tickDefPhys / tickDefMag scalars
    // directly. Session 10 turned those scalars into derived caches
    // sourced from `u.modifiers[]`; the real lifecycle is per-modifier
    // ticksRemaining decrements done HERE, with `_recomputeScalars`
    // refreshing the cached values after pruning expired entries.
    //
    // Why end-of-tick (not start-of-next-tick)? Step F (DoT) has
    // already finished by here, so Shield Wall's tickDefMag from this
    // tick has done its job. Decrement before the next tick starts.
    //
    // Same-source spells with durationTicks: 2+ stay in the list and
    // continue to affect future ticks until they decrement out.
    // Decrement runs on ALL units (incl. dead) for deterministic state;
    // dead units' caches are never read so the extra writes are cheap.
    for (const u of next.units) {
      if (u.modifiers.length === 0) continue;
      let expired = false;
      for (let i = u.modifiers.length - 1; i >= 0; i--) {
        u.modifiers[i].ticksRemaining -= 1;
        if (u.modifiers[i].ticksRemaining <= 0) {
          u.modifiers.splice(i, 1);
          expired = true;
        }
      }
      // Decrementing alone doesn't change the derived scalar value (the
      // scalar reads `value`, not `ticksRemaining`). Only recompute when
      // an expiry actually pruned something.
      if (expired) _recomputeScalars(u);
    }
  }

  // 3 ticks survived — advance round, reset round-scoped state
  next.round += 1;
  next.phase = 'match3';
  next.playerStats = {};
  next.aiStats = {};
  next.playerUsedEls = [];
  next.aiUsedEls = [];

  return { newState: next, events, winner: -1 };
}

/**
 * If the battle has just ended, append a `gameEnd` event and flip the
 * server phase to 'over'. Returns true if the caller should bail out
 * of the remaining tick steps.
 */
function _emitGameEndIfOver(next: ServerState, events: BattleEvent[]): boolean {
  const winner = _checkWinner(next.units, next.bases);
  if (winner === -1) return false;
  events.push({ type: 'gameEnd', winner, dur: 1.5 });
  next.phase = 'over';
  return true;
}

/** Read the `winner` field off the most-recently-pushed gameEnd event. */
function _winnerFromTail(events: BattleEvent[]): number {
  const last = events[events.length - 1];
  if (last && last.type === 'gameEnd') return last.winner;
  return -1;
}

function _checkWinner(units: BattleUnit[], bases: Base[]): number {
  const b0 = bases.find((b) => b.team === 0);
  const b1 = bases.find((b) => b.team === 1);
  const b0Dead = b0 ? b0.destroyed : true;
  const b1Dead = b1 ? b1.destroyed : true;
  const t0Alive = units.some((u) => !u.dead && u.team === 0);
  const t1Alive = units.some((u) => !u.dead && u.team === 1);

  if (b0Dead && b1Dead) return -2;
  if (b0Dead) return 1;
  if (b1Dead) return 0;
  if (!t0Alive && !t1Alive) return -2;
  if (!t0Alive) return 1;
  if (!t1Alive) return 0;
  return -1;
}
